import styled from 'styled-components';
import { palette } from 'styled-tools';

import { fontSize, FontSizes, fontWeight, FontWeights, maxWidth, minWidth } from '@calm-web/design-system';

import HeroCurve from './assets/svg/hero-curve.svg';

const Section = styled.div`
	color: ${palette('gray7')};
	padding-top: 32px;
	padding-bottom: 32px;

	/* It's important that these go in ascending order from mobile -> tablet -> desktop! */
	${minWidth('mobileLarge')} {
		padding-top: 52px;
		padding-bottom: 52px;
	}

	${minWidth('tablet')} {
		padding-top: 72px;
		padding-bottom: 72px;
	}

	${minWidth('desktop')} {
		padding-top: 92px;
		padding-bottom: 92px;
	}
`;

export const Large = styled.div`
	height: 44rem;
	width: 100%;
	position: relative;

	${maxWidth('desktop')} {
		display: none;
	}
`;

export const Row = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	width: 100%;
	max-width: 1200px;
	justify-self: center;
	margin: 0 auto;
	box-sizing: border-box;
	padding: 0 5rem;

	${maxWidth('tablet')} {
		padding: 0 3rem;
		text-align: center;
	}

	${maxWidth('mobileLarge')} {
		padding: 0 1.5rem;
	}
`;

export const HeroRow = styled(Row)`
	min-height: 45rem;
	z-index: 1;

	${maxWidth('tablet')} {
		min-height: 55rem;
	}
`;

export const Column = styled.div<{
	width?: number;
	mobileOrder?: number;
}>`
	display: flex;
	width: ${props => props.width || 50}%;
	justify-content: center;
	flex-wrap: wrap;
	box-sizing: border-box;

	${maxWidth('desktop')} {
		width: 100%;
		padding: 1.5rem 0;
		justify-content: center;
		${props => (props.mobileOrder ? `order: ${props.mobileOrder};` : '')}
	}
`;

export const LeftColumn = styled(Column)`
	align-items: flex-start;
	justify-content: flex-start;
	align-content: flex-start;
	padding-right: 4rem;
`;

export const RightColumn = styled(Column)`
	align-items: flex-start;
`;

export const FeatureList = styled.ul`
	display: flex;
	flex-wrap: wrap;
	list-style: none;
	margin: 0 auto;
	padding-left: 0;
	width: calc(100% - 2.5rem);

	${minWidth('tablet')} {
		margin: 0 auto 2.875rem;
		justify-content: space-between;
	}

	${minWidth('desktop')} {
		flex-direction: column;
		margin: 0;
	}
`;

export const Bullet = styled.li`
	align-items: center;
	color: ${palette('gray7')};
	display: flex;
	flex-direction: row;
	margin-bottom: 1.875rem;
	flex-wrap: nowrap;
	width: 100%;

	&:last-of-type {
		margin-bottom: 2.25rem;
		${minWidth('desktop')} {
			margin-bottom: 0;
		}
	}

	${maxWidth('desktop')} {
		align-items: flex-start;
		flex-direction: row;
		text-align: left;
	}
`;

export const Icon = styled.div`
	width: 4.75rem;
	height: 4.75rem;

	${maxWidth('tablet')} {
		height: 4rem;
		width: 4rem;
	}

	& svg {
		height: 100%;
		width: 100%;
	}
`;

export const BulletCopy = styled.div`
	width: calc(100% - 4.75rem);
	box-sizing: border-box;
	padding-left: 1.5rem;

	${maxWidth('tablet')} {
		padding-left: 1rem;
		width: calc(100% - 4rem);
	}
`;

export const Subheading = styled.h4`
	font-size: 1.5rem;
	${fontWeight(FontWeights.Light)};
	margin-bottom: 2rem;
	text-align: center;

	${minWidth('tablet')} {
		font-size: 1.75rem;
		${fontWeight(FontWeights.Regular)};
	}
`;

export const BodyText = styled.div`
	color: ${palette('gray7')};
	font-size: 1rem;
	${fontWeight(FontWeights.Light)};
	line-height: 1.5;
	margin-bottom: 0;
	text-align: left !important;

	${minWidth('tablet')} {
		font-size: 1.25rem;
	}

	${minWidth('desktop')} {
		margin-bottom: 2.25rem;
		margin-bottom: 0;
	}
`;

export const HeroSection = styled(Section)`
	color: white;
	display: flex;
	flex-wrap: wrap;
	flex-direction: row;
	position: relative;
`;

export const LoginWrapper = styled.div`
	color: ${palette('gray7')};
	text-align: center;
	box-sizing: border-box;
	position: relative;
	display: block;
	max-width: 500px;

	${maxWidth('tablet')} {
		width: 100%;
		margin-bottom: -6rem;
	}
`;

export const MessageWrapper = styled.div`
	${fontSize(FontSizes.xl)};
	${fontWeight(FontWeights.Light)};
`;

export const LoginInner = styled.div`
	width: 100%;
	border-radius: 16px;
	background: ${palette('white')};
	box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.25);
	padding: 2.25rem;
	box-sizing: border-box;

	${maxWidth('tablet')} {
		padding: 1.25rem;
	}
`;

export const LoginTitle = styled.h2`
	font-size: 27px;
	${fontWeight(FontWeights.Medium)};
	margin-top: 0;
	padding-top: 0;
	margin-bottom: 1rem;
`;

export const Logos = styled.div`
	aspect-ratio: 864 / 250;
	height: 5rem;
	margin-bottom: 3rem;
	position: relative;
	align-self: flex-start;

	${maxWidth('tablet')} {
		height: 4rem;
	}
`;

export const Title = styled.h1`
	font-size: 1.75rem;
	${fontWeight(FontWeights.Regular)};
	margin: 0 0 2rem;
	padding: 0;
	width: auto;

	${minWidth('mobileLarge')} {
		font-size: 2.25rem;
	}
`;

export const Subtitle = styled.div`
	${fontSize(FontSizes.base)};
	${fontWeight(FontWeights.Regular)};
	width: 100%;
	${maxWidth('desktop')} {
		text-align: center;
	}
`;

export const SleepSection = styled(Section)`
	color: ${palette('gray7')};

	${maxWidth('tablet')} {
		padding-top: 4rem;
	}
`;

export const SleepTitle = styled.h2`
	${fontSize(FontSizes['3xl'])};
	${fontWeight(FontWeights.Light)};
	margin-bottom: 2.5rem;
`;

export const SubtitleBold = styled.span`
	${fontWeight(FontWeights.Medium)};
`;

export const SubtitleNotBold = styled.span`
	${fontWeight(FontWeights.Light)};
`;

export const SleepDisclaimer = styled.div`
	margin-top: 2.25rem;
	${fontSize(FontSizes.sm)};
	${fontWeight(FontWeights.Medium)};
`;

export const WhatYouGetTitle = styled.h2`
	font-size: 2rem;
	${fontWeight(FontWeights.Light)};
	margin-bottom: 2.75rem;

	${minWidth('mobileLarge')} {
		font-size: 2.75rem;
	}
`;

export const WhatYouGetSection = styled(Section)`
	background: linear-gradient(-180deg, rgb(255, 255, 255) 0%, rgb(239, 240, 255) 100%);
	overflow: hidden;
`;

export const HowToUseSection = styled(Section)``;

export const CenteredRow = styled(Row)`
	justify-content: center;
`;

export const HowToUseTitle = styled.h2`
	${fontSize(FontSizes['3xl'])};
	margin-bottom: 1.25rem;
	font-weight: 100;
`;

export const HowToUseSubtitle = styled.div`
	max-width: 640px;
	${fontWeight(FontWeights.Light)};
	display: block;
	margin-bottom: 3.5rem;

	${maxWidth('mobileLarge')} {
		margin-bottom: 2rem;
	}
`;

export const TileContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-around;
	flex-wrap: wrap;
	max-width: 66rem;
	margin: 0 auto;
`;

export const TileWrapper = styled.div`
	width: 17.5rem;
	height: 20rem;
	position: relative;
	display: flex;
	max-width: 100%;
	border-radius: 10px;
	margin-bottom: 2rem;
	color: ${palette('white')};
	margin-left: 1rem;
	margin-right: 1rem;

	${minWidth('tablet')} {
		width: 13.125rem;
		height: 15rem;
		margin-left: 0.25rem;
		margin-right: 0.25rem;
	}

	${maxWidth('mobileLarge')} {
		margin-right: 0;
		margin-left: 0;
	}
`;

export const TextWrapper = styled.div`
	display: flex;
	align-self: flex-end;
	justify-content: center;
	flex-wrap: wrap;
	text-align: center;
	padding: 2.5rem;
	z-index: 1;

	${minWidth('tablet')} {
		padding: 1.25rem;
	}
`;

export const TileTitle = styled.div`
	font-size: 1.25rem;
	${fontWeight(FontWeights.Medium)};
	margin-bottom: 0.75rem;
`;

export const TileSubtitle = styled.div`
	${fontWeight(FontWeights.Light)};
	flex-wrap: wrap;
	display: flex;
	align-items: center;
	justify-content: center;

	${minWidth('tablet')} {
		font-size: 16px;
	}
`;

export const RatingsTitle = styled.div`
	${fontSize(FontSizes['2xl'])};
	${fontWeight(FontWeights.Light)};
	margin-bottom: 1.75rem;
`;

export const LoginSubtitle = styled.div`
	${fontSize(FontSizes.lg)};
	font-size: 1.25rem;
	margin-bottom: 1.5rem;
`;

export const PaddedContainer = styled.div`
	padding: 4.5rem 0 7rem;
	width: 100%;
	text-align: center;
	display: flex;
	justify-content: center;

	${maxWidth('tablet')} {
		padding: 2rem 0 2rem;
	}
`;

export const Divider = styled.div`
	background: linear-gradient(-225deg, rgb(117, 178, 240) 0%, rgb(178, 117, 240) 100%);
	height: 1px;
	width: 100%;
	margin-bottom: 2rem;
`;

export const FooterContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;

	${maxWidth('tablet')} {
		justify-content: center;
	}
`;

export const FooterText = styled.div`
	color: ${palette('gray5')};
	${fontWeight(FontWeights.Light)};
	padding-right: 5px; // Not noticable, but makes it break at a good spot on tablet
`;

export const Centered = styled.div`
	display: flex;
	margin: 2rem 0 1rem;
	justify-content: center;
	text-align: center;
	flex-wrap: wrap;
`;

export const LoggedInAs = styled.div`
	width: 100%;
	text-align: center;
	margin-top: 0.5rem;
`;

export const BoldLink = styled.span`
	${fontWeight(FontWeights.Demi)};
	cursor: pointer;
`;

export const RatingsText = styled.span`
	height: 15px;
`;

export const UnderlinedLink = styled.a`
	text-decoration: underline;
`;

export const HeroMask = styled(HeroCurve)`
	bottom: 0;
	left: -30%;
	position: absolute;
	right: -30%;
	height: auto;
	width: 160%;

	${minWidth('desktop')} {
		left: 0;
		right: 0;
		width: 100%;
	}
`;

export const FormContainer = styled.div`
	box-sizing: border-box;
`;

export const Heading = styled.h2`
	color: ${palette('gray7')};
	${fontWeight(FontWeights.Regular)};
	margin-bottom: 1rem;
	text-align: center;
	line-height: 1.25;
	font-size: 1.5rem;

	${minWidth('desktop')} {
		font-size: 1.75rem;
	}
`;
